import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Heading,
  Input,
  Select,
  SelectChip,
  Stepper,
  Textarea,
  Toggle,
} from '@openroad/ui';
import { api, newZealandTimeToUtc, unwrap } from '../lib/api';
import { useEndSessionOnAuthFailure } from '../lib/session';

/** `publishBody` allows six; one is required, which is what the button enforces. */
const MAX_PICKUP_POINTS = 6;

/** The ceiling `publishBody` puts on `seatsTotal`, whatever the car seats. */
const MAX_SEATS = 8;

/**
 * `publishBody`'s own bounds on `pricePerSeatCents`, repeated because the form
 * submits with `noValidate` — the `min` on the input is decoration, and without
 * this a typed `-1` enabled the button and earned a 400 nobody could read.
 */
const MIN_PRICE_CENTS = 0;
const MAX_PRICE_CENTS = 100_000;

const CHATTINESS = [
  { value: 'QUIET', label: 'Quiet ride' },
  { value: 'BALANCED', label: 'Happy either way' },
  { value: 'CHATTY', label: 'Up for a chat' },
] as const;

type Chattiness = (typeof CHATTINESS)[number]['value'];

/**
 * Publishing a ride.
 *
 * The screen the design's navigation has always pointed at. Smolit could do
 * this before the form could, which was fine for a copilot and not fine as the
 * only way — a driver who wants to fill in a form should be able to.
 *
 * Adding a vehicle lives here rather than on the profile because it is only
 * ever in the way of this: a member with no vehicle cannot publish, and sending
 * them somewhere else to fix that loses them.
 */
export function PublishScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const places = useQuery({
    queryKey: ['places'],
    queryFn: () => unwrap(api.GET('/places')),
    staleTime: 60 * 60 * 1000,
  });

  const vehicles = useQuery({
    queryKey: ['vehicles'],
    queryFn: () => unwrap(api.GET('/vehicles')),
  });

  const [originId, setOriginId] = useState('');
  const [destinationId, setDestinationId] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [departureAt, setDepartureAt] = useState('');
  const [seatsTotal, setSeatsTotal] = useState(2);
  const [price, setPrice] = useState('');
  const [pickupPoints, setPickupPoints] = useState<string[]>(['']);
  const [description, setDescription] = useState('');
  const [instantBook, setInstantBook] = useState(false);
  const [womenOnly, setWomenOnly] = useState(false);
  const [petsAllowed, setPetsAllowed] = useState(false);
  const [smokingAllowed, setSmokingAllowed] = useState(false);
  const [musicWelcome, setMusicWelcome] = useState(true);
  const [recurringWeekly, setRecurringWeekly] = useState(false);
  const [chattiness, setChattiness] = useState<Chattiness>('BALANCED');
  const [error, setError] = useState<string | null>(null);

  const fleet = vehicles.data?.items ?? [];
  const chosen = fleet.find((vehicle) => vehicle.id === vehicleId);

  // The car is the real ceiling; `publishBody` caps at eight regardless.
  const seatCeiling = Math.min(chosen?.seats ?? MAX_SEATS, MAX_SEATS);

  // Derived rather than clamped into state on every vehicle change: picking a
  // four-seater, setting four seats, then switching to a three-seater has to
  // send three. Displaying the clamped number while submitting the raw one is
  // a 400 the driver cannot see the cause of.
  const seats = Math.min(seatsTotal, seatCeiling);

  const labelled = pickupPoints.map((label) => label.trim()).filter(Boolean);

  const priceCents = Math.round(Number(price) * 100);
  const priceUsable =
    // Whole cents only. `noValidate` means `step` is decoration, so 25.555 was
    // accepted, quietly rounded to 2556, and published as $25.56 while the
    // field still read 25.555 — on the one number this screen promises the
    // driver is what a passenger pays.
    /^\d+([.,]\d{1,2})?$/.test(price.trim()) &&
    Number.isFinite(priceCents) &&
    priceCents >= MIN_PRICE_CENTS &&
    priceCents <= MAX_PRICE_CENTS;

  const publish = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/trips', {
          body: {
            originId,
            destinationId,
            vehicleId,
            // New Zealand time, not the browser's — every time this app prints
            // is Auckland, so the one it reads has to be too.
            departureAt: newZealandTimeToUtc(departureAt),
            seatsTotal: seats,
            pricePerSeatCents: priceCents,
            description: description.trim() || undefined,
            pickupPoints: labelled.map((label) => ({ label })),
            instantBook,
            womenOnly,
            petsAllowed,
            smokingAllowed,
            musicWelcome,
            chattiness,
            recurringWeekly,
          },
        }),
      ),
    onSuccess: (trip) => {
      // The new ride belongs in "your trips" the moment it exists.
      void queryClient.invalidateQueries({ queryKey: ['trips', 'mine'] });
      navigate(`/trips/${trip.id}`);
    },
    onError: (cause) =>
      setError(
        cause instanceof Error
          ? cause.message
          : 'We could not publish that ride. Please try again.',
      ),
  });

  // The mutation's error belongs here as much as the queries'. A session can
  // expire between loading this screen and pressing Publish — the queries
  // already succeeded, so only the POST sees the 401, and without this the
  // member sits on a form that fails every time while the header still shows
  // them signed in.
  const expired = useEndSessionOnAuthFailure(vehicles.error, places.error, publish.error);

  const ready =
    originId !== '' &&
    destinationId !== '' &&
    originId !== destinationId &&
    vehicleId !== '' &&
    departureAt !== '' &&
    priceUsable &&
    labelled.length > 0;

  function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    publish.mutate();
  }

  if (vehicles.isLoading || places.isLoading) {
    return (
      <main className="mx-auto max-w-[680px] px-6 py-14">
        <p className="text-body text-muted">Getting things ready…</p>
      </main>
    );
  }

  // Before the empty-fleet branch, and that order is the whole point: a failed
  // `GET /vehicles` also produces no vehicles, and reading it as "you have no
  // car" would ask a driver to enter a car they have already entered.
  if (vehicles.isError || places.isError) {
    return (
      <main className="mx-auto max-w-[560px] px-6 py-14">
        <Card radius="panel" className="p-7 text-center">
          <Heading level="panel" className="mb-2">
            {expired ? 'Please sign in again' : 'We could not load this screen'}
          </Heading>
          <p className="m-0 mb-6 text-base text-muted">
            {expired
              ? 'Your session has ended. Signing in again will bring everything back.'
              : 'Something went wrong on our side. Try again in a moment.'}
          </p>
          {expired ? null : (
            <Button
              variant="cta"
              onClick={() => {
                void vehicles.refetch();
                void places.refetch();
              }}
            >
              Try again
            </Button>
          )}
        </Card>
      </main>
    );
  }

  // No car, no ride. Nothing else on this screen can be filled in usefully
  // until there is one, so it is the only thing shown.
  if (fleet.length === 0) {
    return (
      <main className="mx-auto max-w-[680px] px-6 pt-10 pb-16">
        <Heading level="panel" as="h1" className="mb-1.5">
          Offer a ride
        </Heading>
        <p className="m-0 mb-6 text-base text-muted">
          First, which car are you driving? You only have to do this once.
        </p>
        <AddVehicleCard onAdded={(vehicle) => setVehicleId(vehicle.id)} />
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-[680px] px-6 pt-10 pb-16">
      <Heading level="panel" as="h1" className="mb-1.5">
        Offer a ride
      </Heading>
      <p className="m-0 mb-6 text-base text-muted">
        Passengers see the price you set. Our fee comes out of it — what you receive is shown on the
        ride once it is up.
      </p>

      <form className="flex flex-col gap-5" onSubmit={submit} noValidate>
        <Card radius="panel" className="flex flex-col gap-4 p-6">
          <div className="flex flex-col gap-4 sm:flex-row">
            <Select
              label="From"
              className="w-full"
              value={originId}
              onChange={(event) => setOriginId(event.target.value)}
            >
              <option value="">Choose a place</option>
              {(places.data?.items ?? []).map((place) => (
                <option key={place.id} value={place.id}>
                  {place.name}
                </option>
              ))}
            </Select>
            <Select
              label="To"
              className="w-full"
              value={destinationId}
              onChange={(event) => setDestinationId(event.target.value)}
            >
              <option value="">Choose a place</option>
              {(places.data?.items ?? []).map((place) => (
                <option key={place.id} value={place.id}>
                  {place.name}
                </option>
              ))}
            </Select>
          </div>

          {originId !== '' && originId === destinationId ? (
            <p className="m-0 text-meta text-danger">
              A ride has to go somewhere other than where it started.
            </p>
          ) : null}

          <Input
            label="Leaving"
            type="datetime-local"
            value={departureAt}
            onChange={(event) => setDepartureAt(event.target.value)}
            hint="New Zealand time."
          />
        </Card>

        <Card radius="panel" className="flex flex-col gap-4 p-6">
          <Select
            label="Car"
            value={vehicleId}
            onChange={(event) => setVehicleId(event.target.value)}
            hint={chosen ? `${chosen.seats} passenger seats` : undefined}
          >
            <option value="">Choose a car</option>
            {fleet.map((vehicle) => (
              <option key={vehicle.id} value={vehicle.id}>
                {vehicle.colour} {vehicle.make} {vehicle.model} ({vehicle.year})
              </option>
            ))}
          </Select>

          <div className="flex items-center justify-between">
            <span className="text-label font-semibold text-ink-2">Seats for passengers</span>
            <Stepper
              label="Seats available"
              shape="square"
              value={seats}
              onValueChange={setSeatsTotal}
              min={1}
              max={seatCeiling}
            />
          </div>

          <Input
            label="Price per seat"
            type="number"
            inputMode="decimal"
            min={0}
            step="0.50"
            placeholder="25.00"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
            hint="In dollars. Ask Smolit what a fair price is for this route."
          />

          {price.trim() !== '' && !priceUsable ? (
            <p className="m-0 text-meta text-danger">
              A seat costs between $0 and ${MAX_PRICE_CENTS / 100}, in whole cents.
            </p>
          ) : null}
        </Card>

        <Card radius="panel" className="flex flex-col gap-4 p-6">
          <div className="flex flex-col gap-1.5">
            <span className="text-label font-semibold text-ink-2">Where you will pick up</span>
            <span className="text-meta text-subtle">
              At least one. Somewhere a passenger can find without ringing you.
            </span>
          </div>

          {pickupPoints.map((label, index) => (
            <div key={index} className="flex gap-2">
              <Input
                className="w-full"
                placeholder="Newmarket Station"
                value={label}
                onChange={(event) =>
                  setPickupPoints((current) =>
                    current.map((existing, at) => (at === index ? event.target.value : existing)),
                  )
                }
              />
              {pickupPoints.length > 1 ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    setPickupPoints((current) => current.filter((_, at) => at !== index))
                  }
                >
                  Remove
                </Button>
              ) : null}
            </div>
          ))}

          {pickupPoints.length < MAX_PICKUP_POINTS ? (
            <Button
              type="button"
              variant="secondary"
              className="self-start"
              onClick={() => setPickupPoints((current) => [...current, ''])}
            >
              Add another stop
            </Button>
          ) : null}
        </Card>

        <Card radius="panel" className="flex flex-col gap-4 p-6">
          <Textarea
            label="Anything passengers should know"
            maxLength={2000}
            placeholder="Leaving from the park and ride, boot space for one bag each."
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />

          <div className="flex flex-col gap-1.5">
            <span className="text-label font-semibold text-ink-2">How chatty are you feeling?</span>
            <div className="flex flex-wrap gap-2">
              {CHATTINESS.map((option) => (
                <SelectChip
                  key={option.value}
                  type="button"
                  selected={chattiness === option.value}
                  onClick={() => setChattiness(option.value)}
                >
                  {option.label}
                </SelectChip>
              ))}
            </div>
          </div>

          <Toggle
            label="Confirm seats straight away"
            description="Otherwise you approve each request yourself."
            checked={instantBook}
            onCheckedChange={setInstantBook}
          />
          <Toggle label="Women only" checked={womenOnly} onCheckedChange={setWomenOnly} />
          <Toggle label="Pets welcome" checked={petsAllowed} onCheckedChange={setPetsAllowed} />
          <Toggle label="Music on" checked={musicWelcome} onCheckedChange={setMusicWelcome} />
          <Toggle
            label="Smoking allowed"
            checked={smokingAllowed}
            onCheckedChange={setSmokingAllowed}
          />
          <Toggle
            label="Recurring trip"
            description="You make this run every week."
            checked={recurringWeekly}
            onCheckedChange={setRecurringWeekly}
          />
        </Card>

        {error ? (
          <p
            role="alert"
            className="m-0 rounded-lg bg-pohutukawa-tint px-3.5 py-3 text-label text-danger"
          >
            {error}
          </p>
        ) : null}

        <Button
          type="submit"
          variant="cta"
          size="hero"
          block
          disabled={!ready || publish.isPending}
        >
          {publish.isPending ? 'Publishing…' : 'Publish this ride'}
        </Button>
      </form>
    </main>
  );
}

interface AddedVehicle {
  id: string;
}

/**
 * The one-off: which car. Kept deliberately short — make, model, year, colour
 * and how many people fit. Amenities are a profile detail and a ride can be
 * published without them.
 */
function AddVehicleCard({ onAdded }: { onAdded: (vehicle: AddedVehicle) => void }) {
  const queryClient = useQueryClient();

  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [colour, setColour] = useState('');
  const [seats, setSeats] = useState(3);
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/vehicles', {
          body: {
            make: make.trim(),
            model: model.trim(),
            year: Number(year),
            colour: colour.trim(),
            seats,
            amenities: [],
          },
        }),
      ),
    onSuccess: async (vehicle) => {
      await queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      onAdded(vehicle);
    },
    onError: (cause) =>
      setError(
        cause instanceof Error ? cause.message : 'We could not save that car. Please try again.',
      ),
  });

  // Same reason as the publish mutation: this card is often the first thing a
  // freshly signed-up member touches, and a 401 here has to end the session
  // rather than read as "we could not save that car".
  useEndSessionOnAuthFailure(add.error);

  const ready =
    make.trim() !== '' && model.trim() !== '' && colour.trim() !== '' && /^\d{4}$/.test(year);

  function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    add.mutate();
  }

  return (
    <Card radius="panel" className="p-6">
      <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <div className="flex flex-col gap-4 sm:flex-row">
          <Input
            label="Make"
            className="w-full"
            placeholder="Toyota"
            value={make}
            onChange={(event) => setMake(event.target.value)}
          />
          <Input
            label="Model"
            className="w-full"
            placeholder="Corolla"
            value={model}
            onChange={(event) => setModel(event.target.value)}
          />
        </div>

        <div className="flex flex-col gap-4 sm:flex-row">
          <Input
            label="Year"
            type="number"
            className="w-full"
            placeholder="2019"
            value={year}
            onChange={(event) => setYear(event.target.value)}
          />
          <Input
            label="Colour"
            className="w-full"
            placeholder="Silver"
            value={colour}
            onChange={(event) => setColour(event.target.value)}
          />
        </div>

        <div className="flex items-center justify-between">
          <span className="text-label font-semibold text-ink-2">
            Seats for passengers, not counting you
          </span>
          <Stepper
            label="Passenger seats"
            shape="square"
            value={seats}
            onValueChange={setSeats}
            min={1}
            max={MAX_SEATS}
          />
        </div>

        {error ? (
          <p
            role="alert"
            className="m-0 rounded-lg bg-pohutukawa-tint px-3.5 py-3 text-label text-danger"
          >
            {error}
          </p>
        ) : null}

        <Button type="submit" variant="cta" size="hero" block disabled={!ready || add.isPending}>
          {add.isPending ? 'Saving…' : 'Save this car'}
        </Button>
      </form>
    </Card>
  );
}
